import * as React from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Skeleton } from '@/components/ui/skeleton';
import { ContainerTypeTable } from '@/components/ContainerTypeTable';
import { cn } from '@/lib/utils';
import { ChevronDown, ChevronRight, UserCircle } from 'lucide-react';
import type { EventAccountListItem } from './EventAccountStockSection';
import { ItemTypeDisplay } from '@/components/ItemType/ItemTypeDisplay';
import { useIsMobile } from '@/hooks/use-mobile';
import i18n from '@/utils/i18n';
import { formatContainerCount } from '@/utils/formatContainerCount';
import SvgIcon from '@/components/Icon/SvgIcon';

export interface StockEntryV2 {
  event_account_id: string;
  container_type_id: string | null; // null when entry is a container station
  container_station_type_id: string | null; // set when entry is a container station
  total_container_stations_ordered: number; // boxes
  total_container_stations_delivered: number; // boxes
  total_container_stations_in_stock: number; // boxes
  total_container_stations_returned: number; // boxes
  total_container_stations_returned_based_on_history: number; // boxes — based on return scan history
  total_containers_ordered: number; // individual containers
  total_containers_delivered: number; // individual containers
  total_containers_in_stock: number; // individual containers
  total_containers_returned: number; // individual containers
}

interface ContainerTypeInfo {
  id: number;
  name: string;
  image_url?: string | null;
  packing_unit: number;
  color_hex_code?: string | null;
}

interface EventAccountStockSectionV2Props {
  eventAccount: EventAccountListItem;
  stockEntries: StockEntryV2[];
  containerTypes: ContainerTypeInfo[];
  isLoading?: boolean;
}

interface StockRowV2 {
  type: string;
  typeId: string;
  imageUrl?: string | null;
  hexCode?: string | null;
  orderedContainers: number;
  orderedBoxes: number;
  // inStock = delivered - returned_based_on_history (boxes only, can be negative)
  inStockBoxes: number;
  handedOutContainers: number; // uses total_containers_delivered
  handedOutBoxes: number; // uses total_container_stations_delivered
  // returned based on scan history (boxes only)
  returnedBoxes: number;
}

// Get the effective type ID: use container_type_id if present, otherwise container_station_type_id
function getTypeId(entry: StockEntryV2): string {
  return entry.container_type_id || entry.container_station_type_id || '';
}

export function EventAccountStockSectionV2({
  eventAccount,
  stockEntries,
  containerTypes,
  isLoading,
}: EventAccountStockSectionV2Props) {
  const [isOpen, setIsOpen] = React.useState(false);
  const isMobile = useIsMobile();

  // Check if total delivered boxes exceed total ordered boxes for this event account
  const isOverdrawn = React.useMemo(() => {
    if (!stockEntries || stockEntries.length === 0) return false;
    const containerEntries = stockEntries.filter((e) => e.container_type_id);
    const totals = containerEntries.reduce(
      (acc, entry) => ({
        ordered: acc.ordered + entry.total_container_stations_ordered,
        delivered: acc.delivered + entry.total_container_stations_delivered,
      }),
      { ordered: 0, delivered: 0 },
    );
    return totals.delivered > totals.ordered;
  }, [stockEntries]);

  // Calculate summary totals for header badges (box counts)
  const summaryTotals = React.useMemo(() => {
    if (!stockEntries || stockEntries.length === 0) {
      return { handedOut: 0, returned: 0, inStock: 0 };
    }
    return stockEntries.reduce(
      (acc, entry) => ({
        handedOut: acc.handedOut + entry.total_container_stations_delivered,
        returned:
          acc.returned +
          entry.total_container_stations_returned_based_on_history,
        // inStock = delivered - returned_based_on_history: can be negative if more boxes were returned than delivered
        inStock:
          acc.inStock +
          entry.total_container_stations_delivered -
          entry.total_container_stations_returned_based_on_history,
      }),
      { handedOut: 0, returned: 0, inStock: 0 },
    );
  }, [stockEntries]);

  // Process stock data into table rows
  const tableRows = React.useMemo<StockRowV2[]>(() => {
    const rows: StockRowV2[] = [];

    if (stockEntries && stockEntries.length > 0) {
      // Group by container type and aggregate
      const containerTypeMap = new Map<
        string,
        {
          typeId: string;
          orderedContainers: number;
          orderedBoxes: number;
          inStockBoxes: number;
          handedOutContainers: number;
          handedOutBoxes: number;
          returnedBoxes: number;
        }
      >();

      stockEntries.forEach((entry) => {
        const typeId = getTypeId(entry);
        const existing = containerTypeMap.get(typeId) || {
          typeId,
          orderedContainers: 0,
          orderedBoxes: 0,
          inStockBoxes: 0,
          handedOutContainers: 0,
          handedOutBoxes: 0,
          returnedBoxes: 0,
        };
        existing.orderedContainers += entry.total_containers_ordered;
        existing.orderedBoxes += entry.total_container_stations_ordered;
        // inStock = delivered - returned_based_on_history (boxes only)
        existing.inStockBoxes +=
          entry.total_container_stations_delivered -
          entry.total_container_stations_returned_based_on_history;
        existing.handedOutContainers += entry.total_containers_delivered;
        existing.handedOutBoxes += entry.total_container_stations_delivered;
        existing.returnedBoxes +=
          entry.total_container_stations_returned_based_on_history;
        containerTypeMap.set(typeId, existing);
      });

      // Convert to rows
      Array.from(containerTypeMap.values()).forEach((item) => {
        const containerType = containerTypes.find(
          (ct) => String(ct.id) === item.typeId,
        );
        rows.push({
          type: containerType?.name || `Type ${item.typeId}`,
          typeId: item.typeId,
          imageUrl: containerType?.image_url || null,
          hexCode: containerType?.color_hex_code || null,
          orderedContainers: item.orderedContainers,
          orderedBoxes: item.orderedBoxes,
          inStockBoxes: item.inStockBoxes,
          handedOutContainers: item.handedOutContainers,
          handedOutBoxes: item.handedOutBoxes,
          returnedBoxes: item.returnedBoxes,
        });
      });
    }

    return rows;
  }, [stockEntries, containerTypes]);

  if (isLoading) {
    return (
      <div className="tw:flex tw:flex-col tw:gap-2">
        <Skeleton className="tw:h-16 tw:w-full tw:rounded-lg" />
      </div>
    );
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <button
            className={cn(
              'tw:bg-white tw:dark:bg-card tw:border tw:border-neutral-200 tw:dark:border-neutral-700 tw:rounded-lg tw:p-4 tw:w-full tw:cursor-pointer tw:hover:bg-neutral-50 tw:dark:hover:bg-neutral-800 tw:transition-colors',
              isMobile
                ? 'tw:flex tw:flex-col tw:gap-3 tw:items-start'
                : 'tw:flex tw:items-center tw:justify-between',
            )}
          >
            {isMobile ? (
              <>
                {/* Mobile: Row 1 - Event Account Name */}
                <div className="tw:flex tw:gap-2 tw:items-center tw:w-full">
                  <UserCircle className="tw:size-5 tw:text-neutral-700 tw:dark:text-neutral-300" />
                  <span className="tw:text-xl tw:font-semibold tw:leading-[1.2] tw:tracking-[-0.4px] tw:text-neutral-900 tw:dark:text-neutral-100">
                    {eventAccount.name || eventAccount.id}
                  </span>
                </div>

                {/* Mobile: Row 2 - Summary Badges and Stock Status */}
                <div className="tw:flex tw:gap-2 tw:items-center tw:flex-wrap">
                  <Badge
                    variant="outline"
                    className="tw:bg-neutral-50 tw:dark:bg-neutral-800 tw:border-neutral-200 tw:dark:border-neutral-600 tw:px-2 tw:py-1 tw:text-xs tw:font-medium tw:gap-1"
                  >
                    <SvgIcon name="arrow-right" size={14} />
                    {summaryTotals.handedOut}
                  </Badge>
                  <Badge
                    variant="outline"
                    className="tw:bg-neutral-50 tw:dark:bg-neutral-800 tw:border-neutral-200 tw:dark:border-neutral-600 tw:px-2 tw:py-1 tw:text-xs tw:font-medium tw:gap-1"
                  >
                    <SvgIcon name="arrow-return" size={14} />
                    {summaryTotals.returned}
                  </Badge>
                  <Badge
                    className={cn(
                      'tw:px-2 tw:py-1 tw:text-xs tw:font-medium tw:gap-1 tw:text-white tw:border-0',
                      // positive: boxes still on-site (warning), zero: all returned (success), negative: over-returned (primary)
                      summaryTotals.inStock > 0
                        ? 'tw:bg-(--color-warning-main)'
                        : summaryTotals.inStock === 0
                          ? 'tw:bg-(--color-success-main)'
                          : 'tw:bg-(--color-primary-main)',
                    )}
                  >
                    <SvgIcon name="abacus-count" size={14} />
                    {summaryTotals.inStock}
                  </Badge>
                  {isOverdrawn && (
                    <Badge variant="destructive">
                      {i18n.t('Event:stockOverdraw')}
                    </Badge>
                  )}
                </div>

                {/* Mobile: Row 3 - Chevron */}
                <div className="tw:flex tw:items-center tw:justify-end tw:w-full">
                  {isOpen ? (
                    <ChevronDown className="tw:size-5 tw:text-neutral-700 tw:dark:text-neutral-300" />
                  ) : (
                    <ChevronRight className="tw:size-5 tw:text-neutral-700 tw:dark:text-neutral-300" />
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Desktop: Left side - Event Account Name and Stock Status */}
                <div className="tw:flex tw:gap-2 tw:items-center">
                  <UserCircle className="tw:size-5 tw:text-neutral-700 tw:dark:text-neutral-300" />
                  <span className="tw:text-xl tw:font-semibold tw:leading-[1.2] tw:tracking-[-0.4px] tw:text-neutral-900 tw:dark:text-neutral-100">
                    {eventAccount.name || eventAccount.id}
                  </span>
                  {isOverdrawn && (
                    <Badge variant="destructive">
                      {i18n.t('Event:stockOverdraw')}
                    </Badge>
                  )}
                </div>

                {/* Desktop: Right side - Summary Badges and Chevron */}
                <div className="tw:flex tw:gap-2 tw:items-center">
                  <Badge
                    variant="outline"
                    className="tw:bg-neutral-50 tw:dark:bg-neutral-800 tw:border-neutral-200 tw:dark:border-neutral-600 tw:px-2 tw:py-1 tw:text-xs tw:font-medium tw:gap-1"
                  >
                    <SvgIcon name="arrow-right" size={14} />
                    {summaryTotals.handedOut}
                  </Badge>
                  <Badge
                    variant="outline"
                    className="tw:bg-neutral-50 tw:dark:bg-neutral-800 tw:border-neutral-200 tw:dark:border-neutral-600 tw:px-2 tw:py-1 tw:text-xs tw:font-medium tw:gap-1"
                  >
                    <SvgIcon name="arrow-return" size={14} />
                    {summaryTotals.returned}
                  </Badge>
                  <Badge
                    className={cn(
                      'tw:px-2 tw:py-1 tw:text-xs tw:font-medium tw:gap-1 tw:text-white tw:border-0',
                      // positive: boxes still on-site (warning), zero: all returned (success), negative: over-returned (primary)
                      summaryTotals.inStock > 0
                        ? 'tw:bg-(--color-warning-main)'
                        : summaryTotals.inStock === 0
                          ? 'tw:bg-(--color-success-main)'
                          : 'tw:bg-(--color-primary-main)',
                    )}
                  >
                    <SvgIcon name="abacus-count" size={14} />
                    {summaryTotals.inStock}
                  </Badge>
                  {isOpen ? (
                    <ChevronDown className="tw:size-5 tw:text-neutral-700 tw:dark:text-neutral-300" />
                  ) : (
                    <ChevronRight className="tw:size-5 tw:text-neutral-700 tw:dark:text-neutral-300" />
                  )}
                </div>
              </>
            )}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="tw:bg-white tw:dark:bg-card tw:border tw:border-neutral-200 tw:dark:border-neutral-700 tw:rounded-lg tw:p-2 tw:mt-2">
            {isLoading ? (
              <div className="tw:p-4 tw:text-center tw:text-neutral-500 tw:dark:text-neutral-400">
                {i18n.t('Common:loading')}
              </div>
            ) : (
              <ContainerTypeTable
                columns={[
                  { key: 'type', label: i18n.t('Common:type'), width: '88px' },
                  {
                    key: 'ordered',
                    label: i18n.t('Event:ordered'),
                    width: 'flex-1',
                    headerContent: (
                      <span className="tw:text-sm tw:font-medium tw:leading-[1.5] tw:text-neutral-900 tw:dark:text-neutral-100 tw:tracking-[0.07px]">
                        {i18n.t('Event:ordered')}
                      </span>
                    ),
                  },
                  {
                    key: 'handed_out',
                    label: i18n.t('Event:handedOut'),
                    width: 'flex-1',
                    headerContent: (
                      <span className="tw:text-sm tw:font-medium tw:leading-[1.5] tw:text-neutral-900 tw:dark:text-neutral-100 tw:tracking-[0.07px]">
                        {i18n.t('Event:handedOut')}
                      </span>
                    ),
                  },
                  {
                    key: 'returned',
                    label: i18n.t('Event:returned'),
                    width: 'flex-1',
                    headerContent: (
                      <span className="tw:text-sm tw:font-medium tw:leading-[1.5] tw:text-neutral-900 tw:dark:text-neutral-100 tw:tracking-[0.07px]">
                        {i18n.t('Event:returned')}
                      </span>
                    ),
                  },
                  {
                    key: 'in_stock',
                    label: i18n.t('Event:inStock'),
                    width: 'flex-1',
                    headerContent: (
                      <span className="tw:text-sm tw:font-medium tw:leading-[1.5] tw:text-neutral-900 tw:dark:text-neutral-100 tw:tracking-[0.07px]">
                        {i18n.t('Event:inStock')}
                      </span>
                    ),
                  },
                ]}
                rows={tableRows.map((row) => {
                  return {
                    id: `container-type-${row.typeId}`,
                    rowHeight: '60px',
                    cardTitle: (
                      <div className="tw:flex tw:gap-2 tw:items-center tw:min-w-0 tw:w-full">
                        <ItemTypeDisplay
                          imageUrl={row.imageUrl}
                          hexCode={row.hexCode}
                          alt={row.type}
                        />
                        <div className="tw:flex tw:items-center tw:h-12 tw:min-w-0 tw:flex-1">
                          <span className="tw:text-xs tw:font-medium tw:leading-[1.5] tw:text-neutral-900 tw:dark:text-neutral-100 tw:tracking-[0.18px] tw:overflow-hidden tw:text-ellipsis tw:whitespace-nowrap tw:block tw:w-full">
                            {row.type}
                          </span>
                        </div>
                      </div>
                    ),
                    cells: [
                      <div
                        key="type"
                        className="tw:flex tw:items-start tw:relative tw:shrink-0"
                      >
                        <ItemTypeDisplay
                          imageUrl={row.imageUrl}
                          hexCode={row.hexCode}
                          alt={row.type}
                        />
                      </div>,
                      <Badge
                        key="ordered"
                        variant="outline"
                        className="tw:bg-neutral-50 tw:dark:bg-neutral-800 tw:border-neutral-200 tw:dark:border-neutral-600 tw:px-2 tw:py-1 tw:text-xs tw:font-medium tw:min-h-[24px]"
                      >
                        {formatContainerCount(
                          row.orderedContainers,
                          row.orderedBoxes,
                        )}
                      </Badge>,
                      <Badge
                        key="handed_out"
                        variant="outline"
                        className="tw:bg-neutral-50 tw:dark:bg-neutral-800 tw:border-neutral-200 tw:dark:border-neutral-600 tw:px-2 tw:py-1 tw:text-xs tw:font-medium tw:min-h-[24px]"
                      >
                        {formatContainerCount(
                          row.handedOutContainers,
                          row.handedOutBoxes,
                        )}
                      </Badge>,
                      <Badge
                        key="returned"
                        variant="outline"
                        className="tw:bg-neutral-50 tw:dark:bg-neutral-800 tw:border-neutral-200 tw:dark:border-neutral-600 tw:px-2 tw:py-1 tw:text-xs tw:font-medium tw:min-h-[24px]"
                      >
                        {row.returnedBoxes}{' '}
                        {row.returnedBoxes === 1
                          ? i18n.t('Event:box')
                          : i18n.t('Event:boxes')}
                      </Badge>,
                      <Badge
                        key="in_stock"
                        variant="outline"
                        className="tw:bg-neutral-50 tw:dark:bg-neutral-800 tw:border-neutral-200 tw:dark:border-neutral-600 tw:px-2 tw:py-1 tw:text-xs tw:font-medium tw:min-h-[24px]"
                      >
                        {row.inStockBoxes}{' '}
                        {row.inStockBoxes === 1
                          ? i18n.t('Event:box')
                          : i18n.t('Event:boxes')}
                      </Badge>,
                    ],
                  };
                })}
                isLoading={isLoading}
                emptyMessage={i18n.t('Event:noStockDataAvailable')}
                headerHeight="54px"
              />
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
